import { Heading, LiliMask } from '@lili/lili-frontend-common';
import { useTranslation } from 'react-i18next';
import { OnboardingFormFields } from '@Types/types.ts';
import { NationalityInput } from '@Components/inputs/NationalityInput.tsx';
import {
  ControlledCheckboxNew,
  ControlledMaskedInput,
} from '@Components/inputs/ControlledInputs.tsx';
import { AgreementToolTip } from '@Pages/happyFlowPages/identity/identityForm/components/AgreementToolTip.tsx';
import { NationalIdInput } from '@Components/inputs/NationalIdInput.tsx';
import { PassportInput } from '@Components/inputs/PassportInput.tsx';
import type { Country } from '@Types/country.ts';
import type { ExtendedUseFormReturn } from '@Hooks/providers/useFormHooks.ts';
import type { IIdentityForm } from '@Lib/Identity/types';

const DEFAULT_DATE_FORMAT = 'MM/DD/YYYY';

interface IdentityNonUsFormProps {
  countryDataList: { title: string; value: string }[];
  selectedCountry: Country | undefined;
  owner?: boolean;
  formMethods: ExtendedUseFormReturn<IIdentityForm>;
}

export const IdentityNonUsForm = ({
  countryDataList,
  selectedCountry,
  owner,
  formMethods,
}: IdentityNonUsFormProps) => {
  const { t } = useTranslation('enJSON');
  const agreementAText = !owner
    ? t('pages.nonUsIdentity.agreementAOwner')
    : t('pages.nonUsIdentity.agreementA');

  const dateFormatHint = DEFAULT_DATE_FORMAT;

  return (
    <>
      <div className='grid phone:gap-[24px]  phone:grid-cols-1 sm:grid-cols-2 sm:gap-[24px]'>
        <NationalityInput
          onNationalityChange={() => {
            formMethods.clearErrors(OnboardingFormFields.NATIONALITY);
            formMethods.resetField(OnboardingFormFields.NATIONAL_ID_NUMBER, { defaultValue: '' });
          }}
          selectedCountry={selectedCountry}
          listItems={countryDataList.filter((country) => country.value !== 'US')}
          onSetError={(errorMsg) => {
            formMethods.setError(OnboardingFormFields.NATIONALITY, { message: errorMsg });
          }}
        />
        {/* TODO: Create LiliDateInput in common */}
        <ControlledMaskedInput
          control={formMethods.control}
          name={OnboardingFormFields.DATE_OF_BIRTH}
          mask={LiliMask.DATE}
          hint={dateFormatHint}
          id={OnboardingFormFields.DATE_OF_BIRTH}
          title={`${t('pages.identity.bdFieldTitle')} (${dateFormatHint})`}
          isPublic={false}
        />
      </div>
      <PassportInput />
      <NationalIdInput
        selectedCountry={selectedCountry}
        helperText={t('pages.nonUsIdentity.nationalIdHelper')}
        helperTextClassName='-mt-[18px]'
      />
      <div className='flex flex-col gap-[8px] items-baseline mt-[24px]'>
        <ControlledCheckboxNew
          control={formMethods.control}
          name={OnboardingFormFields.IDENTITY_NON_US_AGREEMENT_A}
          id={OnboardingFormFields.IDENTITY_NON_US_AGREEMENT_A}
          props={{
            label: (
              <Heading.H10 className='text-text-main-tertiary leading-[14px]'>
                {agreementAText}{' '}
              </Heading.H10>
            ),
            labelAlign: 'center',
          }}
        />
        {owner && (
          <ControlledCheckboxNew
            control={formMethods.control}
            name={OnboardingFormFields.IDENTITY_NON_US_AGREEMENT_B}
            id={OnboardingFormFields.IDENTITY_NON_US_AGREEMENT_B}
            props={{
              label: <AgreementToolTip />,
              labelAlign: 'center',
            }}
          />
        )}
      </div>
    </>
  );
};
